import { TableModalWithLayoutSettings } from '@Components/Tables/TableModalWithLayoutSettings';
import { addressColumns } from '@Constants/TableColumns';
import { useImportContactsForExternalAddress } from '@Hooks/Stammdaten/useImportContactsForExternalAddress';
import { useImportExternalAddress } from '@Hooks/Stammdaten/useImportExternalAddress';
import { type ApiAddress, EnumFrontendLayoutSetting } from '@Model';
import { type ToolbarOptions } from '@synqo/frontend-component-library';
import { useCallback, useMemo, useState } from 'react';
import { useClientNavigate } from '../../../Routing/useClientNavigate';
import { ImportableErpContactsModal } from './ImportableErpContactsModal';

const noToolbarElements: ToolbarOptions[] = [];

interface ImportExternalErpAddressFlowProps {
    isOpen: boolean;
    onClose: () => void;
}

export function ImportExternalErpAddressFlow({ isOpen, onClose }: ImportExternalErpAddressFlowProps) {
    const [selectedExternalAddressId, setSelectedExternalAddressId] = useState<string | null>(null);
    const navigate = useClientNavigate();

    const { loadAddresses, isImporting, importAsync } = useImportExternalAddress();
    const { loadContacts } = useImportContactsForExternalAddress(selectedExternalAddressId ?? '');

    const handleAddressSelect = useCallback(
        (address: ApiAddress) => {
            setSelectedExternalAddressId(address.external_id);
            return true;
        },
        [setSelectedExternalAddressId],
    );

    const handleAddressUnselect = useCallback(() => {
        setSelectedExternalAddressId(null);
        return true;
    }, [setSelectedExternalAddressId]);

    const handleClose = useCallback(() => {
        setSelectedExternalAddressId(null);
        onClose();
    }, [setSelectedExternalAddressId, onClose]);

    const handleImport = useCallback(
        async (selectedContactIds: string[]) => {
            if (!selectedExternalAddressId) return;
            const result = await importAsync({
                external_address_id: selectedExternalAddressId,
                external_contact_person_ids: selectedContactIds,
            });
            handleClose();
            const newId = result?.data?.id;
            if (newId) setTimeout(() => navigate(`/stammdaten/addresses/${newId}`), 0);
        },
        [selectedExternalAddressId, importAsync, handleClose, navigate],
    );

    const addressTableProps = useMemo(
        () => ({
            name: 'ErpAddressList',
            columns: addressColumns,
            onSelect: handleAddressSelect,
            loader: loadAddresses,
            toolbarElements: noToolbarElements,
        }),
        [handleAddressSelect, loadAddresses],
    );

    return (
        <>
            <TableModalWithLayoutSettings<ApiAddress>
                title="Adresse aus ERP importieren"
                isOpen={isOpen && selectedExternalAddressId === null}
                onClose={handleClose}
                layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                tableProps={addressTableProps}
            />
            <ImportableErpContactsModal
                isOpen={isOpen && selectedExternalAddressId !== null}
                title="Ansprechpartner auswählen"
                loadContacts={loadContacts}
                isImporting={isImporting}
                onClose={handleClose}
                onBack={handleAddressUnselect}
                handleImport={handleImport}
            />
        </>
    );
}
